/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { Button, notification, Table, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { useContextState } from "../../context/ContextProvider";
import ActionModal from "../../modals/ActionModal";
import AddRestoreOrgModal from "../../modals/AddRestoreOrgModal";
import {
  getArchivedOrganizationFolders,
  getArchivedOrganizations,
  removeFromArchive,
} from "../../utils/archive_files";
import { removeLastItemFromBreadCrumb } from "../../utils/helperFunctions";
import {
  getOrganizations,
  getQboOrganizations,
} from "../../utils/organization";
import FolderContentDetails from "../Organization/FolderContentDetails";
import FolderContents from "../Organization/FolderContents";

const formatBytes = (megaBytes) => {
  if (!megaBytes) return "0 B";

  // Always use MB for small values
  if (megaBytes < 1024) {
    return `${parseFloat(megaBytes.toFixed(2))} MB`;
  }

  const k = 1024;
  const sizes = ["MB", "GB", "TB"];

  // Calculate which unit to use
  const i = Math.floor(Math.log(megaBytes) / Math.log(k));

  // Make sure we don't exceed array bounds
  const sizeIndex = Math.min(i, sizes.length - 1);

  return (
    parseFloat((megaBytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[sizeIndex]
  );
};

const Archive = () => {
  const { isLoading, getIdTokenClaims } = useAuth0();
  const { orgId, dateFolder } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const archiveProvider = searchParams.get("platform") === "qbo" ? "qbo" : "xero";

  const currentModuleName = searchParams.get("module");
  const currentLogId = searchParams.get("logId");

  const [moduleName, setModuleName] = useState(currentModuleName || "");
  const [logId, setLogId] = useState(currentLogId || "");
  const [organizationName, setOrganizationName] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [restoreOrgSelected, setRestoreOrgSelected] = useState("");
  const [restoreOrgIdSelected, setRestoreOrgIdSelected] = useState(null);
  const [restoreOrgBackupDates, setRestoreOrgBackupDates] = useState([]);
  const [restoreOrganizations, setRestoreOrganizations] = useState([]);
  const [restoreInitialBackupDate, setRestoreInitialBackupDate] = useState(null);
  const { setBreadCrumb, breadCrumb } = useContextState();

  const [selectedRecord, setSelectedRecord] = useState(null);
  // Navigation levels
  const LEVEL = {
    ORGANIZATIONS: 1,
    FOLDERS: 2,
    FILES: 3,
    MODULE_DETAILS: 4,
  };

  // Determine current navigation level based on URL parameters and query params
  const getCurrentLevel = () => {
    if (orgId && dateFolder && currentModuleName && currentLogId) {
      return LEVEL.MODULE_DETAILS;
    } else if (orgId && dateFolder) {
      return LEVEL.FILES;
    } else if (orgId) {
      return LEVEL.FOLDERS;
    } else {
      return LEVEL.ORGANIZATIONS;
    }
  };

  const currentLevel = getCurrentLevel();

  // Sync state with URL params
  useEffect(() => {
    setModuleName(currentModuleName || "");
    setLogId(currentLogId || "");
  }, [currentModuleName, currentLogId, orgId, dateFolder]);

  const resolveArchivedOrgDisplayName = async (token, targetOrgId = orgId) => {
    if (!targetOrgId || !token) return "";
    const rawToken = token.__raw;
    const res = await getArchivedOrganizations(rawToken, archiveProvider);
    const org = (res.data?.accounts || []).find(
      (row) => String(row.id) === String(targetOrgId)
    );
    return org?.account?.trim() || "";
  };

  // Find organization name when we have an orgId
  useEffect(() => {
    const fetchOrgName = async () => {
      if (orgId && currentLevel > LEVEL.ORGANIZATIONS) {
        try {
          const token = await getIdTokenClaims();
          if (!token) return;
          const name = await resolveArchivedOrgDisplayName(token);
          if (name) {
            setOrganizationName(name);
          }
        } catch (error) {
          console.error("Error fetching organization name:", error);
        }
      }
    };

    fetchOrgName();
  }, [orgId, currentLevel, archiveProvider]);

  useEffect(() => {
    let crumbs = [];

    if (currentLevel >= LEVEL.FOLDERS && orgId) {
      //  archive level
      crumbs.push({
        label: organizationName || orgId,
        url: `/archive/${orgId}${platformQuery(archiveProvider)}`,
      });
    }

    if (currentLevel >= LEVEL.FILES && dateFolder) {
      //  folder level
      crumbs.push({
        label: dateFolder,
        url: `/archive/${orgId}/${dateFolder}`,
      });
    }

    if (currentLevel == LEVEL.MODULE_DETAILS && moduleName) {
      //  module level
      crumbs.push({
        label: moduleName,
        url: `/archive/${orgId}/${dateFolder}&module=${moduleName}${
          logId ? `&logId=${logId}` : ""
        }`,
      });
    }

    // Set the breadcrumb state
    setBreadCrumb(crumbs);
  }, [currentLevel, orgId, dateFolder, moduleName, organizationName, logId]);

  // Generate breadcrumb text based on current navigation
  // const breadcrumb = useMemo(() => {
  //   let crumbs = [{ label: "Organizations", url: "/organizations" }];

  //   if (currentLevel >= LEVEL.FOLDERS && orgId) {
  //     // Add organization level
  //     crumbs.push({
  //       label: organizationName || orgId,
  //       url: `/organizations?orgId=${orgId}`,
  //     });
  //   }

  //   if (currentLevel >= LEVEL.FILES && dateFolder) {
  //     // Add folder level
  //     crumbs.push({
  //       label: dateFolder,
  //       url: `/organizations?orgId=${orgId}&folderDate=${dateFolder}`,
  //     });
  //   }

  //   if (currentLevel == LEVEL.MODULE_DETAILS && moduleName) {
  //     // Add module level
  //     crumbs.push({
  //       label: moduleName,
  //       url: `/organizations?orgId=${orgId}&folderDate=${dateFolder}&module=${moduleName}${
  //         logId ? `&logId=${logId}` : ""
  //       }`,
  //     });
  //   }

  //   // Set the breadcrumb state
  //   setBreadCrumb(crumbs);

  //   // For backward compatibility, return a string version
  //   return crumbs.length > 1
  //     ? crumbs.map((crumb) => crumb.label).join(" > ")
  //     : "Organizations";
  // }, [currentLevel, orgId, dateFolder, moduleName, organizationName, logId]);

  const handleModuleSelection = (logId, moduleName) => {
    setLogId(logId);
    setModuleName(moduleName);

    // Update URL params without replacing the history entry
    const newParams = new URLSearchParams(searchParams);
    newParams.set("module", moduleName);
    newParams.set("logId", logId);
    setSearchParams(newParams);
  };

  const handleGoBack = () => {
    switch (currentLevel) {
      case LEVEL.MODULE_DETAILS:
        // From module details to files view
        setModuleName("");
        setLogId("");
        const newParams = new URLSearchParams();
        setSearchParams(newParams, { replace: true });
        navigate(
          `/archive/${orgId}/${dateFolder}${platformQuery(archiveProvider)}`,
          { replace: true }
        );
        setBreadCrumb(removeLastItemFromBreadCrumb(breadCrumb));

        break;

      case LEVEL.FILES:
        // From files view to folders
        navigate(`/archive/${orgId}${platformQuery(archiveProvider)}`, {
          replace: true,
        });
        setBreadCrumb(removeLastItemFromBreadCrumb(breadCrumb));

        break;

      case LEVEL.FOLDERS:
        // From folders to organizations
        navigate("/archive", { replace: true });
        setBreadCrumb([]);
        break;

      default:
        // No back action at organization level
        setBreadCrumb([]);

        break;
    }
  };

  const platformQuery = (platform) =>
    platform === "qbo" ? "?platform=qbo" : "";

  const handleRowClick = ({ id, name, type, platform }) => {
    const provider = platform || archiveProvider;
    if (currentLevel == LEVEL.ORGANIZATIONS) {
      navigate(`/archive/${id}${platformQuery(provider)}`, { replace: false });
    } else if (currentLevel == LEVEL.FOLDERS && type == "FOLDER") {
      navigate(
        `/archive/${orgId}/${name}${platformQuery(archiveProvider)}`,
        { replace: false }
      );
    }
  };

  const formatOrgRows = (organizations, platform = "xero") =>
    organizations.map((org, index) => ({
      key: `${platform}-${org.id}-${index}`,
      id: org.id,
      name: org.account,
      createdAt: org.date,
      platform,
      type: "FOLDER",
    }));

  const formatFolderRows = (data) =>
    data.dates.map((folder, index) => {
      const rawSize = data.folder_size?.[folder.rec_date];
      const bd = data.folder_size_breakdown?.[folder.rec_date];
      const mb = bd?.total_mb ?? (typeof rawSize === "number" ? rawSize : parseFloat(rawSize) || 0);
      return {
        key: index,
        id: folder.xero_id ?? folder.qbo_id ?? folder.id,
        name: folder.rec_date,
        createdAt: folder.date,
        size: formatBytes(mb),
        breakdown: bd,
        type: "FOLDER",
      };
    });

  const fetchFiles = async () => {
    const token = await getIdTokenClaims();
    if (!token) return [];

    const rawToken = token.__raw;

    if (currentLevel == LEVEL.ORGANIZATIONS) {
      const [xeroRes, qboRes] = await Promise.all([
        getArchivedOrganizations(rawToken, "xero"),
        getArchivedOrganizations(rawToken, "qbo"),
      ]);
      return [
        ...formatOrgRows(xeroRes.data?.accounts || [], "xero"),
        ...formatOrgRows(qboRes.data?.accounts || [], "qbo"),
      ];
    }

    if (currentLevel == LEVEL.FOLDERS) {
      const res = await getArchivedOrganizationFolders(rawToken, orgId, archiveProvider);
      return formatFolderRows(res.data || []);
    }

    return [];
  };

  const {
    data: files,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["vault_files", archiveProvider, orgId, dateFolder, currentLevel],
    queryFn: fetchFiles,
    enabled:
      !isLoading &&
      (currentLevel == LEVEL.ORGANIZATIONS || currentLevel == LEVEL.FOLDERS),
    staleTime: 0,
    cacheTime: 0,
  });

  // Force refetch when navigation level changes
  useEffect(() => {
    if (
      !isLoading &&
      (currentLevel == LEVEL.ORGANIZATIONS || currentLevel == LEVEL.FOLDERS)
    ) {
      refetch();
    }
  }, [currentLevel, orgId, dateFolder, archiveProvider]);

  const onConfirmDeleteArchive = async () => {
    try {
      const token = await getIdTokenClaims();

      const response = await removeFromArchive(
        token.__raw,
        orgId,
        selectedRecord.name,
        archiveProvider
      );
      if (response.data.success) {
        // Refresh the data after deletion
        refetch();
        notification.success({
          message: "Removed from Archive",
          description: `${selectedRecord.name} has been successfully removed from the archive.`,
          duration: 2,
          placement: "bottomRight",
        });
      } else {
        notification.error({
          message: "Failed to Remove from Archive",
          description:
            "There was an error removing the backup from the archive. Please try again.",
          duration: 2,
        });
      }
    } catch (e) {
      notification.error({
        message: "Failed to Remove from Archive",
        description:
          "There was an error removing the backup from the archive. Please try again.",
        duration: 2,
      });
    } finally {
      setDeleteModalOpen(false);
    }
  };

  const onRestoreModalClose = () => {
    setRestoreOrgSelected("");
    setRestoreOrgIdSelected(null);
    setRestoreOrgBackupDates([]);
    setRestoreOrganizations([]);
    setRestoreInitialBackupDate(null);
  };

  const handleRestoreSubmit = () => {
    refetch();
    notification.success({
      message: "Restore queued",
      description: "Your restore request has been submitted successfully.",
      duration: 3,
      placement: "bottomRight",
    });
    onRestoreModalClose();
  };

  const onDeleteArchive = (record) => {
    setSelectedRecord(record);
    setDeleteModalOpen(true);
  };

  const onRestoreArchive = async (record) => {
    if (!orgId) return;

    setSelectedRecord(record);
    setRestoreInitialBackupDate(record.name);
    setRestoreOrgIdSelected(orgId);

    try {
      const token = await getIdTokenClaims();
      if (!token) return;

      const crumbLabel = breadCrumb[0]?.label?.trim() || "";
      let displayName =
        organizationName?.trim() ||
        (crumbLabel && crumbLabel !== String(orgId) ? crumbLabel : "") ||
        "";

      if (!displayName || displayName === String(orgId)) {
        displayName = await resolveArchivedOrgDisplayName(token);
      }

      setRestoreOrgSelected(displayName || String(orgId));

      const rawToken = token.__raw;
      const fetchOrgs =
        archiveProvider === "qbo" ? getQboOrganizations : getOrganizations;

      const [orgsRes, foldersRes] = await Promise.all([
        fetchOrgs(rawToken),
        getArchivedOrganizationFolders(rawToken, orgId, archiveProvider),
      ]);

      const accounts = (orgsRes.data?.accounts || []).map((org) => ({
        ...org,
        provider: archiveProvider,
      }));
      setRestoreOrganizations(accounts);

      const archivedDates = foldersRes.data?.dates || [];
      setRestoreOrgBackupDates(
        archivedDates.length > 0
          ? archivedDates
          : (files || []).map((row) => ({ rec_date: row.name }))
      );
    } catch (error) {
      console.error("Error loading restore modal data:", error);
      notification.error({
        message: "Could not open restore",
        description:
          "Failed to load restore data. Please try again.",
        duration: 3,
        placement: "bottomRight",
      });
      onRestoreModalClose();
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (_, record) => (
          <div
            className={`flex gap-2 items-center ${
              record.type == "FOLDER" ? "cursor-pointer" : ""
            }`}
            onClick={() => handleRowClick(record)}
          >
            <ReactSVG
              src={`/assets/icons/${
                record.type == "FOLDER" ? "folder" : "file"
              }.svg`}
            />
            {record.name}
          </div>
        ),
      },
    ];

    if (currentLevel == LEVEL.FOLDERS) {
      baseColumns.push({
        title: "Size",
        dataIndex: "size",
        key: "size",
        render: (size, record) => {
          if (!record.breakdown) {
            return <span>{size || "0 B"}</span>;
          }
          const bd = record.breakdown;
          const hasFilesSub = bd.has_files !== undefined ? !!bd.has_files : false;
          const hasHistorySub = bd.has_history !== undefined ? !!bd.has_history : false;
          const totalMb = bd.total_mb ?? (bd.accounting_mb + (hasFilesSub ? bd.files_mb : 0) + (hasHistorySub ? bd.history_mb : 0));

          const tooltipContent = (
            <div className="p-1 text-xs text-left leading-relaxed text-gray-200" style={{ minWidth: 180 }}>
              <div className="font-semibold text-white border-b border-gray-600 pb-1 mb-1.5 flex items-center justify-between">
                <span>Storage Breakdown</span>
                <span className="font-bold text-sky-400">{totalMb.toFixed(2)} MB</span>
              </div>
              <div className="flex justify-between items-center py-0.5">
                <span className="text-gray-300">Accounting:</span>
                <span className="font-medium text-white">{bd.accounting_mb.toFixed(2)} MB</span>
              </div>
              {hasFilesSub && (
                <div className="flex justify-between items-center py-0.5">
                  <span className="text-gray-300">Files & Attachments:</span>
                  <span className="font-medium text-white">{bd.files_mb.toFixed(2)} MB</span>
                </div>
              )}
              {hasHistorySub && (
                <div className="flex justify-between items-center py-0.5">
                  <span className="text-gray-300">History & Notes:</span>
                  <span className="font-medium text-white">{bd.history_mb.toFixed(2)} MB</span>
                </div>
              )}
            </div>
          );

          return (
            <Tooltip title={tooltipContent} placement="topLeft">
              <div className="cursor-pointer inline-flex items-center py-1">
                <span className="font-semibold text-sm text-gray-800 hover:text-blue-600 transition-colors">
                  {size}
                </span>
              </div>
            </Tooltip>
          );
        },
      });
      baseColumns.push({
        title: "Action",
        dataIndex: "action",
        key: "action",
        align: "right",
        render: (_, record) => (
          <div className="flex items-center justify-end gap-4">
            <Button
              onClick={(e) => onDeleteArchive(record, e)}
              className="text-xs flex items-center gap-2 px-4 py-1.5 text-gray-800 bg-white border border-gray-300 rounded-lg font-bold"
            >
              <ReactSVG src={"/assets/icons/archive.svg"} />
              Delete
            </Button>
            <Button
              onClick={(e) => onRestoreArchive(record, e)}
              className="text-xs flex items-center gap-2 px-4 py-1.5 text-gray-800 bg-white border border-gray-300 rounded-lg font-bold"
            >
              <ReactSVG src={"/assets/icons/archive.svg"} />
              Restore
            </Button>
          </div>
        ),
      });
    }

    return baseColumns;
  }, [currentLevel, organizationName, breadCrumb, orgId]);

  // Determine if back button should be shown
  const showBackButton = currentLevel === LEVEL.FOLDERS;

  // const handleBack = () => {
  //   if (
  //     nestedHeader?.page ===
  //     organisationPage.ORGANISATION_FOLDER_CONTENTS_DETAILS
  //   ) {
  //     // Go back to folder contents
  //     navigate(
  //       `/organizations?orgId=${selectedOrgId}&folderDate=${selectedFolderDate}`
  //     );
  //   } else if (
  //     nestedHeader?.page === organisationPage.ORGANISATION_FOLDER_CONTENTS
  //   ) {
  //     // Go back to folder
  //     navigate(`/organizations?orgId=${selectedOrgId}`);
  //   } else if (nestedHeader?.page === organisationPage.ORGANISATION_FOLDER) {
  //     // Go back to accounts
  //     navigate("/organizations");
  //   }
  // };

  return (
    <>
      {showBackButton && (
        <div className="flex items-center justify-between my-2">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleGoBack}
              className="flex items-center"
              type="default"
            >
              <ReactSVG src="/assets/icons/arrow-left.svg" />
              <span className="ml-2">Back</span>
            </Button>

            <div className="flex items-center">
              {breadCrumb.map((item, index) => {
                const isLastItem = index === breadCrumb.length - 1;
                return (
                  <div key={index} className="flex items-center">
                    <p className={isLastItem ? "font-semibold" : ""}>
                      {item.label}
                    </p>
                    {!isLastItem && <span className="mx-2">&gt;</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* // Delete archive use case */}
      <ActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={onConfirmDeleteArchive}
        itemName={""}
        actionType="delete"
      />

      <AddRestoreOrgModal
        organizationId={restoreOrgIdSelected}
        organizationName={restoreOrgSelected}
        organizations={restoreOrganizations}
        backupDates={restoreOrgBackupDates}
        onModalClose={onRestoreModalClose}
        onRestoreSubmit={handleRestoreSubmit}
        initialBackupDate={restoreInitialBackupDate}
        type={archiveProvider}
      />

      {/* Organization or Folder listing */}
      {(currentLevel == LEVEL.ORGANIZATIONS ||
        currentLevel == LEVEL.FOLDERS) && (
        <Table
          loading={isFetching}
          dataSource={files || []}
          columns={columns}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            defaultPageSize: 10,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          rowKey="key"
        />
      )}

      {/* File contents */}
      {currentLevel == LEVEL.FILES && (
        <FolderContents
          orgId={orgId}
          date={dateFolder}
          onModuleEnter={handleModuleSelection}
          onBack={handleGoBack}
          type={archiveProvider}
        />
      )}

      {/* Module details */}
      {currentLevel == LEVEL.MODULE_DETAILS && (
        <FolderContentDetails
          orgId={orgId}
          date={dateFolder}
          moduleName={moduleName}
          logId={logId}
          onBack={handleGoBack}
          type={archiveProvider}
        />
      )}
    </>
  );
};

export default Archive;
